/* eslint-disable react/require-default-props */
import React, { useRef, useState, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { Button } from '@ui';
import { Popup } from '../../Popup/Popup';
import './BaseFilterStyles.scss';
/**
 * BaseFilter Component
 * Shared anchor-button + Popup wrapper for all popup-based filter molecules.
 * Owns the three visual states from Figma (node-id=886-1581):
 *   - Idle: gray background, label only
 *   - Active (with value): light-purple background, label + selectedDisplay
 *   - Open: purple-outline border
 * @version 1.0.0
 * @since 2026-05-04
 */
export function BaseFilter({ label, isActive, selectedDisplay, count, onReset, resetLabel = 'Reset', disabled = false, children, onOpenChange }) {
    const [isOpen, setIsOpen] = useState(false);
    const anchorRef = useRef(null);
    const ignoreToggleRef = useRef(false);
    const suppressNextClickRef = useRef(false);
    const hasEmittedOpenChangeRef = useRef(false);
    const handleAnchorMouseDown = useCallback((event) => {
        if (disabled || !isOpen)
            return;
        // Clicking the anchor while open should close once and not reopen on click.
        suppressNextClickRef.current = true;
        setIsOpen(false);
        event.preventDefault();
    }, [disabled, isOpen]);
    const handleToggle = useCallback(() => {
        if (disabled)
            return;
        if (suppressNextClickRef.current) {
            suppressNextClickRef.current = false;
            return;
        }
        if (ignoreToggleRef.current)
            return;
        if (isOpen) {
            setIsOpen(false);
            return;
        }
        setIsOpen(true);
    }, [disabled, isOpen]);
    useEffect(() => {
        if (!hasEmittedOpenChangeRef.current) {
            hasEmittedOpenChangeRef.current = true;
            return;
        }
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(isOpen);
    }, [isOpen, onOpenChange]);
    const handleClose = useCallback(() => {
        var _a;
        // Prevent click-away + anchor click races from reopening immediately.
        ignoreToggleRef.current = true;
        setIsOpen(false);
        (_a = anchorRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        window.setTimeout(() => {
            ignoreToggleRef.current = false;
        }, 0);
    }, []);
    const anchorClass = classNames('qmb-ui-base-filter__anchor', {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'qmb-ui-base-filter__anchor--active': isActive && !isOpen,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'qmb-ui-base-filter__anchor--open': isOpen,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'qmb-ui-base-filter__anchor--disabled': disabled
    });
    const handleResetClick = useCallback(() => {
        onReset === null || onReset === void 0 ? void 0 : onReset();
        handleClose();
    }, [onReset, handleClose]);
    return (React.createElement("div", { className: "qmb-ui-base-filter" },
        React.createElement(Button, { ref: anchorRef, className: anchorClass, onClick: handleToggle, onMouseDown: handleAnchorMouseDown, disabled: disabled, "aria-expanded": isOpen, "aria-haspopup": "dialog" },
            React.createElement("span", { className: "qmb-ui-base-filter__content" },
                React.createElement("span", { className: "qmb-ui-base-filter__header" },
                    React.createElement("span", { className: "qmb-ui-base-filter__label" }, label),
                    count != null && count > 0 && React.createElement("span", { className: "qmb-ui-base-filter__count" }, count)),
                isActive && !isOpen && selectedDisplay != null && (React.createElement("span", { className: "qmb-ui-base-filter__selected-display" }, selectedDisplay))),
            React.createElement("i", { className: "fa-light fa-angle-down qmb-ui-base-filter__chevron", "aria-hidden": "true" })),
        React.createElement(Popup, { anchor: anchorRef.current, show: isOpen, onClose: handleClose, position: "bottom-left" },
            React.createElement("div", { className: "qmb-ui-base-filter__popup-content" },
                typeof children === 'function' ? children(handleClose) : children,
                onReset && (React.createElement("div", { className: "qmb-ui-base-filter__popup-footer" },
                    React.createElement(Button, { variant: "highlighted", onClick: handleResetClick },
                        React.createElement("i", { className: "fa-light fa-arrow-rotate-left", "aria-hidden": "true" }),
                        ` ${resetLabel}`)))))));
}
